import React, { useState, useEffect, useRef } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { FaBars, FaTimes, FaChevronDown, FaUser, FaSignOutAlt, FaMoon, FaSun, FaCalendarAlt, FaPlus, FaMinus, FaBell, FaQrcode } from 'react-icons/fa';
import { motion, AnimatePresence } from 'framer-motion';
import pb from '../pocketbase';
import AuthModal from './AuthModal';
import './Navbar.css';
import logoImg from '../assets/logo.png';

const Navbar = () => {
    const [isOpen, setIsOpen] = useState(false);
    const [scrolled, setScrolled] = useState(false);
    const [isAuthModalOpen, setIsAuthModalOpen] = useState(false);
    const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
    const [user, setUser] = useState(pb.authStore.isValid ? pb.authStore.model : null);

    const [mobileDropdown, setMobileDropdown] = useState(null);
    const [isNotifOpen, setIsNotifOpen] = useState(false);
    const [isQrOpen, setIsQrOpen] = useState(false);
    const notifRef = useRef(null);
    const userMenuRef = useRef(null);

    // Disable background scroll when QR Lightbox is open
    useEffect(() => {
        if (isQrOpen || isOpen) {
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = 'unset';
        }

        // Cleanup on unmount
        return () => {
            document.body.style.overflow = 'unset';
        };
    }, [isQrOpen, isOpen]);

    // Close Dropdowns when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (notifRef.current && !notifRef.current.contains(event.target)) {
                setIsNotifOpen(false);
            }
            if (userMenuRef.current && !userMenuRef.current.contains(event.target)) {
                setIsUserMenuOpen(false);
            }
        };

        const handleOpenLogin = () => setIsAuthModalOpen(true);
        const handleOpenQr = () => setIsQrOpen(true);

        document.addEventListener('mousedown', handleClickOutside);
        document.addEventListener('openLoginModal', handleOpenLogin);
        document.addEventListener('openQrModal', handleOpenQr);

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
            document.removeEventListener('openLoginModal', handleOpenLogin);
            document.removeEventListener('openQrModal', handleOpenQr);
        };
    }, []);

    // Theme State
    const [theme, setTheme] = useState(localStorage.getItem('theme') || 'light');

    useEffect(() => {
        document.body.className = theme === 'dark' ? 'dark-mode' : '';
        localStorage.setItem('theme', theme);
    }, [theme]);

    const toggleTheme = () => {
        setTheme(prev => prev === 'light' ? 'dark' : 'light');
    };

    const location = useLocation();

    // Handle scroll effect
    useEffect(() => {
        const handleScroll = () => {
            setScrolled(window.scrollY > 20);
        };
        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    useEffect(() => {
        return pb.authStore.onChange((token, model) => {
            setUser(pb.authStore.isValid ? model : null);
        });
    }, []);

    const logout = () => {
        pb.authStore.clear();
    };

    // Check if we are on the home page styling
    const isHome = location.pathname === '/';

    const navLinks = [
        { name: 'Accueil', path: '/' },
        { name: 'Nettoyage voiture', path: '/nettoyage-voiture' },
        { name: 'Nettoyage mobiliers', path: '/nettoyage-mobiliers' },
        { name: 'Avant-Après', path: '/avant-apres' },
        { name: 'Tarifs', path: '/tarifs' },
        { name: 'Contact', path: '/contact' },
    ];

    const toggleMobileDropdown = (name) => {
        if (mobileDropdown === name) {
            setMobileDropdown(null);
        } else {
            setMobileDropdown(name);
        }
    };

    return (
        <>
            <nav className={`navbar ${scrolled ? 'scrolled' : ''} ${!isHome ? 'navbar-dark' : ''}`}>
                <div className="container navbar-inner">
                    {/* LEFT: Logo */}
                    <div className="navbar-left">
                        <Link to="/" className="logo-container">
                            <img src={logoImg} alt="RoulezPropre Logo" className="navbar-logo-img" />
                        </Link>
                    </div>

                    {/* CENTER: Desktop Menu */}
                    <div className="navbar-center nav-links">
                        {navLinks.map((link) => (
                            <div key={link.name} className="nav-group">
                                {link.subItems ? (
                                    <div className="nav-item-container">
                                        <NavLink
                                            to={link.path}
                                            className={({ isActive }) => `nav-item ${isActive ? 'active' : ''}`}
                                        >
                                            {link.name} <FaChevronDown size={12} />
                                        </NavLink>
                                        <div className="dropdown-menu">
                                            {link.subItems.map(subItem => (
                                                <Link key={subItem.name} to={subItem.path} className="dropdown-item">
                                                    {subItem.name}
                                                </Link>
                                            ))}
                                        </div>
                                    </div>
                                ) : (
                                    <NavLink
                                        to={link.path}
                                        className={({ isActive }) => `nav-item ${isActive ? 'active' : ''}`}
                                    >
                                        {link.name}
                                    </NavLink>
                                )}
                            </div>
                        ))}
                    </div>

                    {/* RIGHT: User & Lang & Mobile Toggle */}
                    <div className="navbar-right">
                        {/* 1. CTA Button (Always first in right section) */}
                        <Link
                            to="/reservation"
                            className="cta-btn"
                        >
                            Prendre RDV
                        </Link>

                        <div className="nav-icons-group">
                            {/* 2. Theme (Lune) */}
                            <button onClick={toggleTheme} className="nav-icon-item mobile-visible theme-btn" title="Mode Sombre/Clair">
                                {theme === 'light' ? <FaMoon /> : <FaSun style={{ color: '#f59e0b' }} />}
                            </button>

                            {/* 3. Cloche (Notifications) */}
                            <div className="nav-icon-item mobile-visible" style={{ position: 'relative' }} ref={notifRef}>
                                <button
                                    onClick={() => setIsNotifOpen(!isNotifOpen)}
                                    className="theme-btn"
                                    title="Notifications"
                                    style={{ position: 'relative' }}
                                >
                                    <FaBell />
                                    <span style={{ position: 'absolute', top: '8px', right: '8px', width: '8px', height: '8px', background: '#e11d48', borderRadius: '50%', border: '1px solid white' }}></span>
                                </button>

                                {isNotifOpen && (
                                    <div className="user-dropdown-menu" style={{ width: '300px', right: '-60px', padding: '0', cursor: 'default' }}>
                                        <div style={{ padding: '15px 20px', borderBottom: '1px solid rgba(0,0,0,0.05)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                            <span style={{ fontWeight: '700', fontSize: '0.95rem', color: '#1f2937' }}>Notifications</span>
                                            <span style={{ fontSize: '0.8rem', color: '#2563eb', cursor: 'pointer', fontWeight: '500' }}>Tout lu</span>
                                        </div>
                                        <div style={{ padding: '40px 20px', textAlign: 'center', color: '#64748b', fontSize: '0.9rem', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '10px' }}>
                                            <div style={{ width: '40px', height: '40px', borderRadius: '50%', background: '#f1f5f9', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#94a3b8' }}>
                                                <FaBell size={18} />
                                            </div>
                                            <p>Aucune nouvelle notification</p>
                                        </div>
                                    </div>
                                )}
                            </div>

                            {/* 4. Bonhomme (User) */}
                            <div className="nav-icon-item user-item" style={{ position: 'relative' }} ref={userMenuRef}>
                                {user ? (
                                    <>
                                        <button
                                            className="user-btn logged-in-avatar"
                                            onClick={() => setIsUserMenuOpen(!isUserMenuOpen)}
                                            title="Mon compte"
                                        >
                                            {user.name ? user.name.charAt(0).toUpperCase() : (user.email ? user.email.charAt(0).toUpperCase() : 'U')}
                                        </button>

                                        {isUserMenuOpen && (
                                            <div className="user-dropdown-menu">
                                                {user.role === 'admin' && (
                                                    <Link
                                                        to="/admin"
                                                        className="user-dropdown-item"
                                                        onClick={() => setIsUserMenuOpen(false)}
                                                        style={{ color: '#f97316', fontWeight: 'bold' }}
                                                    >
                                                        Espace Admin
                                                    </Link>
                                                )}
                                                <Link
                                                    to="/profile"
                                                    className="user-dropdown-item"
                                                    onClick={() => setIsUserMenuOpen(false)}
                                                >
                                                    Mon compte
                                                </Link>
                                                <button
                                                    onClick={() => {
                                                        logout();
                                                        setIsUserMenuOpen(false);
                                                    }}
                                                    className="user-dropdown-item logout-btn"
                                                >
                                                    Se déconnecter
                                                </button>
                                            </div>
                                        )}
                                    </>
                                ) : (
                                    <button className="user-btn" onClick={() => setIsAuthModalOpen(true)}>
                                        <FaUser />
                                    </button>
                                )}
                            </div>

                            {/* 5. QR Code */}
                            <div className="qr-code-wrapper desktop-only">
                                <div className="qr-trigger-icon" onClick={() => setIsQrOpen(true)}>
                                    <FaQrcode />
                                    <span className="qr-pulse"></span>
                                </div>
                                <div className="qr-dropdown-premium">
                                    <div className="qr-glass-card">
                                        <p className="qr-title">Scan & Book</p>
                                        <div className="qr-image-container">
                                            <img
                                                src={`https://api.qrserver.com/v1/create-qr-code/?size=150x150&data=${window.location.origin}/reservation&color=1f2937`}
                                                alt="QR Reservation"
                                            />
                                        </div>
                                        <p className="qr-desc">Cliquez pour agrandir</p>
                                        <div className="qr-arrow"></div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <button
                            onClick={() => setIsOpen(!isOpen)}
                            className={`mobile-toggle ${isOpen ? 'open' : ''}`}
                        >
                            {isOpen ? <FaTimes /> : <FaBars />}
                        </button>
                    </div>
                </div>

                {/* Mobile Menu Drawer - Premium Glass Version */}
                <AnimatePresence>
                    {isOpen && (
                        <>
                            {/* Backdrop */}
                            <motion.div
                                initial={{ opacity: 0 }}
                                animate={{ opacity: 1 }}
                                exit={{ opacity: 0 }}
                                onClick={() => setIsOpen(false)}
                                className="drawer-backdrop"
                            />

                            {/* Drawer */}
                            <motion.div
                                initial={{ x: '100%', opacity: 0 }}
                                animate={{ x: 0, opacity: 1 }}
                                exit={{ x: '100%', opacity: 0 }}
                                transition={{ type: "spring", damping: 25, stiffness: 200 }}
                                className="mobile-drawer"
                            >
                                <div className="drawer-header">
                                    <img src={logoImg} alt="RoulezPropre Logo" className="navbar-logo-img" style={{ height: '40px', width: 'auto' }} />
                                </div>

                                <div className="drawer-links-container">
                                    {navLinks.map((link, index) => (
                                        <motion.div
                                            key={link.name}
                                            initial={{ opacity: 0, x: 20 }}
                                            animate={{ opacity: 1, x: 0 }}
                                            transition={{ delay: 0.1 + index * 0.05 }}
                                            className="drawer-item-wrapper"
                                        >
                                            {link.subItems ? (
                                                <div className="drawer-group">
                                                    <div className="drawer-group-header" onClick={() => toggleMobileDropdown(link.name)}>
                                                        <span className="drawer-link">{link.name}</span>
                                                        {mobileDropdown === link.name ? <FaMinus size={14} /> : <FaPlus size={14} />}
                                                    </div>

                                                    <AnimatePresence>
                                                        {mobileDropdown === link.name && (
                                                            <motion.div
                                                                initial={{ height: 0, opacity: 0 }}
                                                                animate={{ height: 'auto', opacity: 1 }}
                                                                exit={{ height: 0, opacity: 0 }}
                                                                className="drawer-sublinks"
                                                            >
                                                                {link.subItems.map(subItem => (
                                                                    <Link
                                                                        key={subItem.name}
                                                                        to={subItem.path}
                                                                        onClick={() => setIsOpen(false)}
                                                                        className="drawer-sublink"
                                                                    >
                                                                        {subItem.name}
                                                                    </Link>
                                                                ))}
                                                            </motion.div>
                                                        )}
                                                    </AnimatePresence>
                                                </div>
                                            ) : (
                                                <Link
                                                    to={link.path}
                                                    onClick={() => setIsOpen(false)}
                                                    className="drawer-link"
                                                >
                                                    {link.name}
                                                </Link>
                                            )}
                                        </motion.div>
                                    ))}
                                </div>

                                <motion.div
                                    className="drawer-footer"
                                    initial={{ opacity: 0, y: 20 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ delay: 0.5 }}
                                >
                                    <Link
                                        to="/reservation"
                                        onClick={() => setIsOpen(false)}
                                        className="drawer-cta-btn"
                                    >
                                        <FaCalendarAlt /> Prendre RDV
                                    </Link>
                                    <button
                                        className="drawer-secondary-btn"
                                        onClick={() => { 
                                            setIsOpen(false); 
                                            if (user) {
                                                window.location.href = '/profile';
                                            } else {
                                                setIsAuthModalOpen(true);
                                            }
                                        }}
                                    >
                                        <FaUser /> {user ? 'Mon Compte' : 'Se connecter'}
                                    </button>
                                </motion.div>
                            </motion.div>
                        </>
                    )}
                </AnimatePresence>
            </nav>

            <AuthModal isOpen={isAuthModalOpen} onClose={() => setIsAuthModalOpen(false)} />

            {/* QR Code Fullscreen Lightbox */}
            <AnimatePresence>
                {isQrOpen && (
                    <motion.div
                        className="qr-lightbox-overlay"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        onClick={() => setIsQrOpen(false)}
                    >
                        <motion.div
                            className="qr-lightbox-card"
                            initial={{ scale: 0.8, opacity: 0, y: 20 }}
                            animate={{ scale: 1, opacity: 1, y: 0 }}
                            exit={{ scale: 0.8, opacity: 0, y: 20 }}
                            transition={{ type: "spring", damping: 25, stiffness: 300 }}
                            onClick={(e) => e.stopPropagation()}
                        >
                            <button className="qr-lightbox-close" onClick={() => setIsQrOpen(false)}>
                                <FaTimes />
                            </button>

                            <div className="qr-lightbox-content">
                                <div className="qr-lightbox-header">
                                    <div className="logo-circle large">RP</div>
                                    <h3>Réservation Instantanée</h3>
                                    <p>Pointez votre appareil photo sur l'écran pour scanner</p>
                                </div>

                                <div className="qr-large-container">
                                    <img
                                        src={`https://api.qrserver.com/v1/create-qr-code/?size=300x300&data=${window.location.origin}/reservation&color=1f2937`}
                                        alt="QR Reservation Large"
                                    />
                                    <div className="qr-corner tl"></div>
                                    <div className="qr-corner tr"></div>
                                    <div className="qr-corner bl"></div>
                                    <div className="qr-corner br"></div>
                                </div>

                                <div className="qr-lightbox-footer">
                                    <span className="qr-link-text">{window.location.host}/reservation</span>
                                    <div className="qr-status-badge">
                                        <span className="status-dot"></span>
                                        QR Code Sécurisé
                                    </div>
                                </div>
                            </div>
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>
        </>
    );
};

export default Navbar;
